export const TODAY_WORK_QUEUE_TIME_ZONE = 'America/Los_Angeles';

export const TODAY_WORK_QUEUE_STATES = Object.freeze([
  'action_required',
  'progressing',
  'completed',
]);

const STATE_RANK = new Map(TODAY_WORK_QUEUE_STATES.map((value, index) => [value, index]));
const STATE_TONES = Object.freeze({
  action_required: 'blue',
  progressing: 'yellow',
  completed: 'green',
});

const CONTAINER_STATUS_TONES = Object.freeze({
  arrived: 'arrived',
  arrived_at_port: 'arrived',
  available: 'available',
  available_for_pickup: 'available',
  delivered_to_warehouse: 'delivered',
  empty_returned: 'empty-returned',
  pending: 'pending',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  not_arrived: 'neutral',
});

const WORK_QUEUE_ACTION_LABELS = Object.freeze({
  clerk_port_intake: 'Enter ETA and Ocean Status',
  clerk_lfd: 'Update LFD',
  finance_initial: 'Enter CTF and TMF status',
  dispatch_planning: 'Plan loaded movement and assign Driver',
  customer_report_empty: 'Report Container empty',
  dispatch_empty_planning: 'Plan Empty Return and assign Driver',
  driver_empty_return_correction: 'Correct Empty Return evidence',
  dispatch_empty_return_review: 'Review Empty Return evidence',
  finance_completion: 'Complete Finance record',
  return_empty_lfd_at_risk: 'Empty Returned',
});

const WORK_QUEUE_TODO_FOOTER_NOTES = Object.freeze({
  completed: 'Task already submitted. 任务已提交。',
  read_only: 'View only for your role. 当前角色仅可查看。',
});

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function firstDefined(source, ...keys) {
  for (const key of keys) {
    if (source?.[key] !== undefined) return source[key];
  }
  return undefined;
}

function textValue(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function statusKey(value) {
  return textValue(value).toLowerCase().replaceAll('-', '_').replaceAll(' ', '_');
}

function nonNegativeInteger(value) {
  return Number.isSafeInteger(value) && value >= 0 ? Number(value) : null;
}

function todoFieldLabel(field) {
  const acronyms = new Set(['ctf', 'eta', 'lfd', 'pod', 'tmf']);
  return field.split('_').filter(Boolean).map((part) => {
    const normalized = part.toLowerCase();
    if (acronyms.has(normalized)) return normalized.toUpperCase();
    return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
  }).join(' ');
}

function normalizedTodoWorker(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.freeze({
    uid: textValue(firstDefined(source, 'uid')),
    email: textValue(firstDefined(source, 'email')),
    role: textValue(firstDefined(source, 'role')),
    account: textValue(firstDefined(source, 'account')),
  });
}

function explicitTodoCompletion(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const complete = firstDefined(value, 'complete', 'isComplete', 'is_complete');
  return typeof complete === 'boolean' ? complete : null;
}

function normalizedRequiredFields(action) {
  const source = action && typeof action === 'object' && !Array.isArray(action) ? action : {};
  const values = firstDefined(source, 'requiredFields', 'required_fields');
  if (!Array.isArray(values)) return [];
  return values.map(textValue).filter(Boolean);
}

function normalizedTodoItems(itemsValue, action) {
  const sourceItems = Array.isArray(itemsValue) ? itemsValue : [];
  const requiredFields = new Set(normalizedRequiredFields(action));
  return Object.freeze(sourceItems.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const field = textValue(firstDefined(value, 'field'));
    const complete = explicitTodoCompletion(value);
    if (!field || complete === null) return [];
    const suppliedLabel = textValue(firstDefined(value, 'label'));
    const label = suppliedLabel || (requiredFields.has(field) ? todoFieldLabel(field) : field);
    return [Object.freeze({ field, label, complete })];
  }));
}

function normalizedTodoSubmission(value, action) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const id = textValue(firstDefined(value, 'id'));
  const status = textValue(firstDefined(value, 'status'));
  return Object.freeze({
    id,
    status: status === 'completed' ? 'completed' : 'pending',
    worker: normalizedTodoWorker(firstDefined(value, 'worker')),
    items: normalizedTodoItems(firstDefined(value, 'items'), action),
    confirmedAt: textValue(firstDefined(value, 'confirmedAt', 'confirmed_at')) || null,
  });
}

export function normalizeTodayWorkQueueTodoProjection(input) {
  const projection = firstDefined(input, 'todoProjection', 'todo_projection');
  if (!projection || typeof projection !== 'object' || Array.isArray(projection)) return null;
  const action = firstDefined(input, 'action');
  const submissionsValue = firstDefined(projection, 'submissions');
  const submissions = Object.freeze((Array.isArray(submissionsValue) ? submissionsValue : [])
    .map((value) => normalizedTodoSubmission(value, action))
    .filter(Boolean));
  const status = textValue(firstDefined(projection, 'status'));
  return Object.freeze({
    actionId: textValue(firstDefined(projection, 'actionId', 'action_id')),
    revision: nonNegativeInteger(firstDefined(
      projection,
      'revision',
      'expectedRevision',
      'expected_revision',
    )),
    status: status === 'completed' ? 'completed' : 'pending',
    worker: normalizedTodoWorker(firstDefined(projection, 'worker')),
    items: normalizedTodoItems(firstDefined(projection, 'items'), action),
    confirmedBy: firstDefined(projection, 'confirmedBy', 'confirmed_by')
      ? normalizedTodoWorker(firstDefined(projection, 'confirmedBy', 'confirmed_by'))
      : null,
    confirmedAt: textValue(firstDefined(projection, 'confirmedAt', 'confirmed_at')) || null,
    submissions,
  });
}

function instant(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? new Date(value.getTime()) : null;
  }
  if (typeof value?.toDate === 'function') {
    try {
      return instant(value.toDate());
    } catch {
      return null;
    }
  }
  if (Number.isFinite(value?._seconds)) {
    return instant((Number(value._seconds) * 1_000)
      + Math.floor(Number(value._nanoseconds || 0) / 1_000_000));
  }
  if (Number.isFinite(value?.seconds)) {
    return instant((Number(value.seconds) * 1_000)
      + Math.floor(Number(value.nanoseconds || 0) / 1_000_000));
  }
  if ((typeof value !== 'string' && typeof value !== 'number')
    || (typeof value === 'string' && !value.trim())) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function resolveNow(now) {
  const parsed = instant(typeof now === 'function' ? now() : now);
  if (!parsed) throw new TypeError('Work Queue requires a valid controlled clock.');
  return parsed;
}

export function losAngelesDateKey(value) {
  const parsed = instant(value);
  if (!parsed) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TODAY_WORK_QUEUE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsed);
  const values = Object.fromEntries(parts
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function dayOrdinal(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey || '')) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function normalizedCompletion(input) {
  const source = firstDefined(input, 'canonicalCompletion', 'canonical_completion') || {};
  const completedAt = instant(firstDefined(
    source,
    'emptyReturnedAt',
    'empty_returned_at',
    'emptyReturnedAtMillis',
    'empty_returned_at_millis',
  ));
  return Object.freeze({
    status: textValue(firstDefined(source, 'status')),
    isEmptyReturned: firstDefined(source, 'isEmptyReturned', 'is_empty_returned') === true,
    emptyReturnedAt: completedAt?.toISOString() || null,
  });
}

function completionState(workflowStatus, completion, now) {
  if (workflowStatus !== 'empty_returned'
    || completion.status !== 'empty_returned'
    || completion.isEmptyReturned !== true
    || !completion.emptyReturnedAt) return null;
  const nowOrdinal = dayOrdinal(losAngelesDateKey(now));
  const completedOrdinal = dayOrdinal(losAngelesDateKey(completion.emptyReturnedAt));
  if (nowOrdinal === null || completedOrdinal === null) return null;
  const age = nowOrdinal - completedOrdinal;
  return age >= 0 && age < 7 ? 'completed' : 'expired';
}

export function normalizeTodayWorkQueueItem(input, { now = new Date() } = {}) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new TypeError('Work Queue item must be an object.');
  }
  const clock = resolveNow(now);
  const id = textValue(firstDefined(input, 'id'));
  const containerId = textValue(firstDefined(input, 'containerId', 'container_id'));
  const containerNo = textValue(firstDefined(input, 'containerNo', 'container_no'));
  if (!id || !containerId || !containerNo) {
    throw new TypeError('Work Queue item has an invalid identity.');
  }

  const workflowStatus = textValue(firstDefined(
    input,
    'workflowStatus',
    'workflow_status',
    'container_status',
  ));
  const oceanStatus = textValue(firstDefined(input, 'oceanStatus', 'ocean_status'));
  const inputState = textValue(firstDefined(input, 'queueState', 'queue_state'));
  const completion = normalizedCompletion(input);
  const completionResult = completionState(workflowStatus, completion, clock);
  if (completionResult === 'expired' && inputState !== 'action_required') return null;
  const queueState = inputState === 'action_required'
    ? 'action_required'
    : completionResult === 'completed' ? 'completed' : 'progressing';
  const relevantAt = instant(firstDefined(
    input,
    'relevantAt',
    'relevant_at',
    'relevantAtMillis',
    'relevant_at_millis',
  ));
  const targetType = textValue(firstDefined(input, 'targetType', 'target_type'));
  const targetId = textValue(firstDefined(input, 'targetId', 'target_id'));
  const targetField = textValue(firstDefined(input, 'targetField', 'target_field')) || null;
  const nextAction = textValue(firstDefined(input, 'nextAction', 'next_action'));
  const atRisk = firstDefined(input, 'atRisk', 'at_risk') === true;
  const todoProjection = normalizeTodayWorkQueueTodoProjection(input);
  const containerStatusKey = statusKey(oceanStatus) || statusKey(workflowStatus);
  const statusTone = atRisk ? 'at-risk' : queueState === 'completed'
    ? 'empty-returned'
    : CONTAINER_STATUS_TONES[containerStatusKey]
      || (queueState === 'action_required' ? 'arrived' : 'available');

  return Object.freeze({
    id,
    containerId,
    containerNo,
    workflowStatus,
    oceanStatus,
    relevantAt: relevantAt?.toISOString() || null,
    queueState,
    stateTone: STATE_TONES[queueState],
    statusTone,
    targetType,
    targetId,
    targetField,
    nextAction,
    atRisk,
    todoProjection,
    canonicalCompletion: completion,
    completedDaysAgo: completionResult === 'completed' && completion.emptyReturnedAt
      ? dayOrdinal(losAngelesDateKey(clock)) - dayOrdinal(losAngelesDateKey(completion.emptyReturnedAt))
      : null,
  });
}

// The rail is two different lists depending on who is looking (Alan, 2026-08-03).
//
// Staff work from it, so it shows only what still needs a hand - the red and blue
// rows - and those stay until they are done. A completed row on a working list is
// just something to scroll past.
//
// The boss and the customer read it, so it shows only what finished today, and it
// empties overnight. They are not being asked to act; they are being told what got
// done, and a week of history turns that into a list nobody reads.
const WORKING_QUEUE_ROLES = new Set(['clerk', 'dispatcher']);
const REPORTING_QUEUE_ROLES = new Set(['admin', 'finance', 'customer']);

export function todayWorkQueueRoleFilter(role) {
  const token = typeof role === 'string' ? role.trim().toLowerCase() : '';
  if (WORKING_QUEUE_ROLES.has(token)) {
    return (item) => item.queueState === 'action_required';
  }
  if (REPORTING_QUEUE_ROLES.has(token)) {
    return (item) => item.queueState === 'completed' && item.completedDaysAgo === 0;
  }
  // An unknown role gets the unfiltered list rather than an empty one: showing
  // nothing looks like a broken panel, and this rail carries no data a signed-in
  // session is not already entitled to.
  return () => true;
}

export function buildTodayWorkQueueViewModel(items, { now = new Date(), role = '' } = {}) {
  if (!Array.isArray(items)) throw new TypeError('Work Queue items must be an array.');
  const clock = resolveNow(now);
  const keep = todayWorkQueueRoleFilter(role);
  const normalized = items
    .map((value, sourceIndex) => ({
      item: normalizeTodayWorkQueueItem(value, { now: clock }),
      sourceIndex,
    }))
    .filter(({ item }) => item !== null && keep(item));

  normalized.sort((left, right) => {
    const stateDifference = STATE_RANK.get(left.item.queueState)
      - STATE_RANK.get(right.item.queueState);
    if (stateDifference !== 0) return stateDifference;
    const leftTime = instant(left.item.relevantAt)?.getTime();
    const rightTime = instant(right.item.relevantAt)?.getTime();
    if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
      return left.item.queueState === 'completed'
        ? rightTime - leftTime
        : leftTime - rightTime;
    }
    return left.sourceIndex - right.sourceIndex;
  });

  const queueItems = Object.freeze(normalized.map(({ item }) => item));
  return Object.freeze({
    timeZone: TODAY_WORK_QUEUE_TIME_ZONE,
    total: queueItems.length,
    items: queueItems,
    counts: Object.freeze(Object.fromEntries(TODAY_WORK_QUEUE_STATES.map((state) => [
      state,
      queueItems.filter((item) => item.queueState === state).length,
    ]))),
  });
}

function displayTime(value, locale) {
  const parsed = instant(value);
  if (!parsed) return Object.freeze({ date: '—', clock: '—', full: '—' });
  const normalizedLocale = locale === 'zh-CN' ? 'zh-CN' : 'en-US';
  const date = new Intl.DateTimeFormat(normalizedLocale, {
    timeZone: TODAY_WORK_QUEUE_TIME_ZONE,
    month: 'short',
    day: '2-digit',
  }).format(parsed);
  const clock = new Intl.DateTimeFormat(normalizedLocale, {
    timeZone: TODAY_WORK_QUEUE_TIME_ZONE,
    // 24-hour everywhere (Alan, 2026-08-11).
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);
  return Object.freeze({ date, clock, full: `${date}, ${clock}` });
}

function displayStatus(item) {
  if (item.queueState === 'completed') return 'Empty Returned';
  if (item.queueState === 'action_required' && item.nextAction) {
    return WORK_QUEUE_ACTION_LABELS[item.nextAction]
      || item.nextAction.split('_').filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(' ');
  }
  const source = textValue(item.oceanStatus) || textValue(item.workflowStatus)
    || (item.queueState === 'action_required' ? 'action_required' : 'in_progress');
  return source.split('_').filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function todayWorkQueueCardIntent(item) {
  const id = textValue(firstDefined(item, 'id'));
  const containerId = textValue(firstDefined(item, 'containerId', 'container_id'));
  const containerNo = textValue(firstDefined(item, 'containerNo', 'container_no'));
  const queueState = textValue(firstDefined(item, 'queueState', 'queue_state'));
  const atRisk = firstDefined(item, 'atRisk', 'at_risk') === true;
  if (!id || !containerId || !containerNo) {
    throw new TypeError('Work Queue interaction requires a valid item.');
  }
  return Object.freeze({
    kind: 'quick_navigate',
    payload: Object.freeze({
      containerId,
      containerNo,
      targetField: textValue(firstDefined(item, 'targetField', 'target_field')) || null,
      openTodo: queueState === 'action_required' || atRisk,
    }),
  });
}

function secureRequestId() {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== 'function') {
    throw new TypeError('A secure request-id generator is required.');
  }
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createTodayWorkQueueCallbackContract({
  onSubmitTodo = () => undefined,
  onQuickNavigate = () => undefined,
  createRequestId = secureRequestId,
} = {}) {
  if (typeof onSubmitTodo !== 'function'
    || typeof onQuickNavigate !== 'function'
    || typeof createRequestId !== 'function') {
    throw new TypeError('Work Queue callbacks must be functions.');
  }
  const retryRequestIds = new Map();

  return Object.freeze({
    submitTodo(item) {
      const containerId = textValue(firstDefined(item, 'containerId', 'container_id'));
      const projection = firstDefined(item, 'todoProjection', 'todo_projection');
      const actionId = textValue(firstDefined(projection, 'actionId', 'action_id'));
      const expectedRevision = nonNegativeInteger(firstDefined(
        projection,
        'revision',
        'expectedRevision',
        'expected_revision',
      ));
      if (!containerId || !actionId || expectedRevision === null) {
        throw new TypeError('Todo confirmation requires action, Container, and revision data.');
      }
      const retryKey = `${actionId}:${expectedRevision}`;
      let requestId = retryRequestIds.get(retryKey);
      if (!requestId) {
        requestId = textValue(createRequestId());
        if (!requestId) throw new TypeError('Todo confirmation requires a valid request id.');
        retryRequestIds.set(retryKey, requestId);
      }
      const payload = Object.freeze({
        actionId,
        containerId,
        expectedRevision,
        requestId,
      });
      let result;
      try {
        result = onSubmitTodo(payload);
      } catch (error) {
        throw error;
      }
      if (result && typeof result.then === 'function') {
        return Promise.resolve(result).then((value) => {
          retryRequestIds.delete(retryKey);
          return value;
        });
      }
      retryRequestIds.delete(retryKey);
      return result;
    },
    quickNavigate(item) {
      const intent = todayWorkQueueCardIntent(item);
      return onQuickNavigate(intent.payload);
    },
  });
}

function cardMarkup(item, locale) {
  const timestamp = displayTime(item.relevantAt, locale);
  const status = displayStatus(item);
  const intent = todayWorkQueueCardIntent(item);
  const timeMarkup = `<time${item.relevantAt ? ` datetime="${escapeHtml(item.relevantAt)}"` : ''}><span class="today-work-queue-card__date">${escapeHtml(timestamp.date)}</span><span class="today-work-queue-card__clock">${escapeHtml(timestamp.clock)}</span></time>`;
  return `<li class="today-work-queue__item"><button type="button" class="today-work-queue-card today-work-queue-card--${escapeHtml(item.queueState.replaceAll('_', '-'))} today-work-queue-card--tone-${escapeHtml(item.statusTone)}${item.atRisk ? ' today-work-queue-card--at-risk' : ''}" data-testid="today-work-queue-card" data-work-queue-container-id="${escapeHtml(item.containerId)}" data-work-queue-id="${escapeHtml(item.id)}" data-work-queue-target-type="${escapeHtml(item.targetType)}" data-work-queue-target-id="${escapeHtml(item.targetId)}" data-work-queue-target-field="${escapeHtml(item.targetField)}" data-work-queue-next-action="${escapeHtml(item.nextAction)}" data-work-queue-intent="${escapeHtml(intent.kind)}" data-queue-state="${escapeHtml(item.queueState)}" data-state-tone="${escapeHtml(item.stateTone)}" data-status-tone="${escapeHtml(item.statusTone)}" aria-label="${escapeHtml(`${item.containerNo}, ${status}, ${timestamp.full}`)}"><strong>${escapeHtml(item.containerNo)}</strong><span class="today-work-queue-card__status">${escapeHtml(status)}</span>${timeMarkup}</button></li>`;
}

function todoPanelMarkup(item, { canSubmitTodo }) {
  const projection = item.todoProjection;
  const items = projection?.items || [];
  const list = !projection
    ? '<p class="today-work-queue-todo__empty" role="status">Task instructions unavailable.</p>'
    : items.length === 0
      ? '<p class="today-work-queue-todo__empty" role="status">No task instructions supplied.</p>'
      : `<ol class="today-work-queue-todo__list" role="list">${items.map((todo) => (
        `<li class="today-work-queue-todo__item" data-todo-field="${escapeHtml(todo.field)}"><span aria-hidden="true">•</span><strong>${escapeHtml(todo.label)}</strong></li>`
      )).join('')}</ol>`;
  const canSubmit = canSubmitTodo === true
    && projection?.actionId
    && projection?.revision !== null
    && projection?.status !== 'completed';
  const footerNote = projection
    ? (projection.status === 'completed'
      ? WORK_QUEUE_TODO_FOOTER_NOTES.completed
      : WORK_QUEUE_TODO_FOOTER_NOTES.read_only)
    : '';
  const footer = canSubmit
    ? `<footer class="today-work-queue-todo__footer"><button type="button" class="today-work-queue-todo__submit" data-work-queue-submit-todo data-action-id="${escapeHtml(projection.actionId)}" data-container-id="${escapeHtml(item.containerId)}" data-expected-revision="${escapeHtml(projection.revision)}">Submit</button></footer>`
    : footerNote
      ? `<footer class="today-work-queue-todo__footer"><p class="today-work-queue-todo__note" role="status">${escapeHtml(footerNote)}</p></footer>`
      : '';

  return `<section class="today-work-queue-todo" data-testid="today-work-queue-todo-panel" aria-label="Task reminder" data-work-queue-todo-for="${escapeHtml(item.id)}"><header class="today-work-queue-todo__header"><h2>TO DO</h2><p class="today-work-queue-todo__container"><strong data-todo-container-number>${escapeHtml(item.containerNo)}</strong></p></header>${list}${footer}</section>`;
}

export function workQueueWorkspaceRoute(item) {
  const containerId = textValue(firstDefined(item, 'containerId', 'container_id'));
  const targetType = textValue(firstDefined(item, 'targetType', 'target_type'));
  const targetId = textValue(firstDefined(item, 'targetId', 'target_id'));
  const nextAction = textValue(firstDefined(item, 'nextAction', 'next_action'));
  const queueState = textValue(firstDefined(item, 'queueState', 'queue_state'));
  if (queueState === 'action_required') return '';
  if (containerId && nextAction === 'return_empty_lfd_at_risk') {
    return `#/queue-action/${encodeURIComponent(containerId)}~return_empty_lfd_at_risk`;
  }
  return containerId ? `#/inspect/${encodeURIComponent(containerId)}~progress` : '';
}

export function renderTodayWorkQueueMarkup(viewModel, {
  locale = 'en',
  activeQueueId = '',
  canSubmitTodo = true,
} = {}) {
  if (!viewModel || !Array.isArray(viewModel.items) || !Number.isInteger(viewModel.total)) {
    throw new TypeError('A valid Work Queue view model is required.');
  }
  const normalizedLocale = locale === 'zh-CN' ? 'zh-CN' : 'en';
  const activeItem = viewModel.items.find((item) => item.id === activeQueueId
    && (item.queueState === 'action_required' || item.atRisk));
  const todoPanel = activeItem
    ? todoPanelMarkup(activeItem, {
      canSubmitTodo: canSubmitTodo === true,
    })
    : '';
  // The pin control was removed by request. The pinned state machinery in
  // app.js is left in place: it reads through optional chaining, so it is inert
  // without the button, and the CSS still keys off data-pinned.
  return `<aside class="today-work-queue" data-testid="today-work-queue" aria-label="Work Queue"><ol class="today-work-queue__list" role="list">${viewModel.items.map((item) => cardMarkup(item, normalizedLocale)).join('')}</ol>${todoPanel}</aside>`;
}
